import type { ReactNode } from 'react';
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
  type DialogProps,
} from 'react-aria-components';
import { cn } from '@/lib/utils';

/**
 * The Detail_Panel and any other true dialog in the app compose these React Aria
 * Components wrappers, per design.md §"Relationship between React Aria Components
 * and shadcn/ui": React Aria supplies the *behaviour* R18.6 requires — focus trap
 * on open, focus restore to the trigger on close, `Esc` to dismiss, correct
 * `role="dialog"` and `aria-modal` semantics — while the visual shell is styled
 * from the §1 tokens. shadcn's own Dialog (Radix) is kept for simple confirmations;
 * the composite screens that need Aria's focus management use these instead, which
 * is exactly the split the design draws.
 */
export { DialogTrigger as AriaDialogTrigger };

export interface AriaModalDialogProps extends DialogProps {
  /** Whether the modal is open, when controlled from parent state. */
  isOpen?: boolean | undefined;
  /** Open-state change handler for controlled usage. */
  onOpenChange?: ((isOpen: boolean) => void) | undefined;
  /** Dismiss when the overlay behind the dialog is clicked. Defaults to true. */
  isDismissable?: boolean | undefined;
  /** Slot the dialog against the right edge as a drawer (Detail_Panel), else centre it. */
  variant?: 'drawer' | 'centre' | undefined;
  /**
   * Extra classes merged onto the `Modal` surface (width, shadow, animation).
   * Lets a caller pin surface dimensions the UI spec fixes (e.g. the Detail_Panel
   * drawer's 620px width and slide timing) without changing the shared defaults.
   */
  modalClassName?: string | undefined;
  /** Extra classes merged onto the scrim overlay (e.g. an exact scrim colour). */
  overlayClassName?: string | undefined;
  /** Omit the default inner padding when the dialog manages its own regions (drawer). */
  unpadded?: boolean | undefined;
  children: ReactNode;
}

export function AriaModalDialog({
  isOpen,
  onOpenChange,
  isDismissable = true,
  variant = 'centre',
  modalClassName,
  overlayClassName,
  unpadded = false,
  className,
  children,
  ...dialogProps
}: AriaModalDialogProps): React.JSX.Element {
  return (
    <ModalOverlay
      {...(isOpen !== undefined ? { isOpen } : {})}
      {...(onOpenChange ? { onOpenChange } : {})}
      isDismissable={isDismissable}
      className={cn(
        'fixed inset-0 z-50 flex bg-navy/25 backdrop-blur-[1px] data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in data-[exiting]:fade-out',
        overlayClassName,
      )}
    >
      <Modal
        className={cn(
          'bg-panel shadow-drawer outline-none',
          variant === 'drawer'
            ? 'ml-auto h-full w-[520px] max-w-[90vw] data-[entering]:slide-in-from-right data-[exiting]:slide-out-to-right'
            : 'm-auto max-h-[90vh] w-full max-w-lg rounded-card data-[entering]:zoom-in-95 data-[exiting]:zoom-out-95',
          modalClassName,
        )}
      >
        <Dialog
          {...dialogProps}
          className={cn(
            'flex h-full flex-col outline-none',
            unpadded ? '' : 'overflow-auto p-4',
            className,
          )}
        >
          {children}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
